import axiosInstance from "./axiosInstance";

export interface BufferConfig {
  publicId: string;
  type: string;
  venueId: number;
  venueName: string;
  fromVenueId?: number; 
  toVenueId?: number;  
  fromPortId?: number;  
  toPortId?: number;   
  portId: number | null;
  portName: string | null;
  checkInBuffer: string;
  checkOutBuffer: string;
  isActive: boolean;
}

export interface VenueApi {
  venueId: number;
  publicId: string; 
  venueName: string;
  contactNo: string;
  emailAddress: string;
  description: string;
  timeZone: string;
  isActive: boolean;
}

export interface PortApi {
  portId: number;
  publicId: string;
  portName: string;
  portNameArabic: string;
  portShortName: string;
  arrivalShortName: string;
  departureShortName: string;
  portType: "A" | "R" | "W"; 
  isActive: boolean;
  deletedBy: string | null;
  deletedAt: string | null;
}

export interface MiqaatApi {
  id: number;
  miqaatName: string;
  miqaatCode: string;
  rotationName: string;
  requireRaza: boolean;
  rotationId: string;
  arrivalDate: string;
  departureDate: string;
  availableQuota: number;
  totalQuota: number;
}

export interface CutoffConfig {
  publicId: string;
  daysBeforeCutoff: number;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

export interface VenueScheduleApi {
  tourVenueScheduleId: number;
  publicId: string;
  tourId: number;
  venueId: number;
  venueName: string;
  hotelPropertyId?: number;
  hotelName?: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
}

export interface RoomAvailabilityRoomApi {
  roomSelectionPublicId: string;
  propertyRoomTypeId: number;
  propertyRoomTypePublicId: string;
  roomTypeName: string;
  minCapacity: number;
  maxCapacity: number;
  requestedNumber: number;
  roomAllocated: number;
}

export interface RoomAvailabilityPropertyApi {
  propertyId: number;
  propertyPublicId: string;
  propertyName: string;
  rooms: RoomAvailabilityRoomApi[];
}

export interface RoomAvailabilityApi {
  venueId: number;
  venueName: string;
  checkInDate: string;
  checkOutDate: string;
  properties: RoomAvailabilityPropertyApi[];
}

export interface TourApi {
  tourId: number;
  parent_id: string | null;
  publicId: string;
  tourCode: string;
  tourName: string;
  tourType: number;
  tourTypeName: string;
  tourCategory: number | null;
  rotationId: string;
  rotationName: string;
  tourOperatorId: number;
  tourOperatorName: string;
  officeName: string;
  noOfApplicants: number;
  faizAccommodation: boolean;
  accommodationPreference?: number;
  specialAccommodationRequest?: string;
  isArrivalConfirmed: boolean;
  isDepartureConfirmed: boolean;
  status: number;
  isLocked: boolean;
  statusName: string;
  action: string;
  isActive: boolean;
  arrivalDate: string;
  departureDate: string;
  venueCount: number;
  duration: number;
  durationDays: number;
  accommodationStatus: "R" | "Y" | "O" | "G";
  transportStatus: "R" | "Y" | "O" | "G";
  paxStatus: "R" | "O" | "G";
  itineraryStatus: "R" | "Y" | "G";
  createdAt: string;
  venueSchedules: VenueScheduleApi[];
  roomAvailabilities: RoomAvailabilityApi[];
}

export interface TourListResponse {
  items: TourApi[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
  pageNumber: number;
}

export interface CreateTourPayload {
  parent_id: string | null;
  tourName: string;
  tourType: number;
  tourCategory: number | null;
  rotationId: number | string;
  tourOperatorId: number;
  noOfApplicants: number;
  faizAccommodation: boolean;

  accommodationPreference?: number;
  specialAccommodationRequest?: string;
  termsAndConditions?: string;

  notes?: string;

  arrivalTransportMode: number;
  arrivalFlightNo: string;
  arrivalTime: string;
  arrivalPortId: number;
  arrivalDate: string;
  arrivalBookingRef: string; 
  arrivalAirline: string;

  departureTransportMode: number;
  departureFlightNo: string;
  departureTime: string;
  departurePortId: number;
  departureDate: string;
  departureBookingRef: string;
  departureAirline: string;

  isArrivalConfirmed: boolean;
  isDepartureConfirmed: boolean;

  venueSchedules: {
    venueId: number;
    hotelPropertyId?: number;
    hotelName?: string;
    checkInDate: string;
    checkInTime: string;
    checkOutDate: string;
    checkOutTime: string;
  }[];

  roomAvailabilities?: {
    venueId: number;
    checkInDate: string;
    checkOutDate: string;
    properties: {
      propertyId: number;
      rooms: {
        propertyRoomTypeId: number;
        requestedNumber: number;
      }[];
    }[];
  }[];

  createdBy: number;
}

export interface PortDetailApi {
  portId: number;
  publicId: string;
  portName: string;
  portNameArabic: string;
  portShortName: string;
  arrivalShortName: string;
  departureShortName: string;
  portType: "A" | "R" | "W";
  isActive: boolean;
  blocks: {
    portBlockId: number;
    fromDate: string;
    toDate: string;
    fromTime: string;
    toTime: string;
    reason: string;
  }[];
}

interface PartnerApi {
  isMainOperator: boolean;
  permissionExpireDate: string | null;

  tourOperatorTypeId: number | null;
  isInclusive: boolean;
  isPublic: boolean;
  permanentDiscount: number | null;
}

interface TourOperatorWithDocumentsApi {
  tourOperatorTypeId: number;
  partners: PartnerApi[];
}

export interface TourStatsApi {
  totalTours: number;
  approved: number;
  pending: number;
  rejected: number;
  totalApplicants: number;
}

export interface IntercityCheckoutApi {
  interCityCheckoutId: number;   
  publicId: string;
  tourType: number;
  fromVenueId: number;
  fromVenueName: string;
  toVenueId: number | null;
  toVenueName: string;
  checkoutTime: string;
  travelTime?: string | null;
  startDate: string | null;
  endDate: string | null;
  miqaatId: number | null;       
  miqaatCode: string | null;     
  miqaatName: string | null;    
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedBy: number | null;      
  updatedAt: string | null;
}

export interface TourAvailabilityRecommendationApi {
  id: number;
  recommendPublicId: string;
  tourId: number;
  requestedStartDate: string;
  requestedEndDate: string;
  isAvailable: boolean;
  reason: string;
  acceptedAt: string | null;
  recommendedOn: string;
}

export interface TourServiceApi {
  id: number;
  serviceId: number;
  serviceName: string;
  description: string;
  serviceType: string;
  isMandatory: boolean;
  isDefaultChecked: boolean;
  isActive: boolean;
  createdBy: number;
  createdOn: string;
}
 
export interface OptionalApplicantServiceApi {
  applicantId: number;
  services: TourServiceApi[];
}

export interface RoomAllocationApplicantApi {
  tourRoomAllocationApplicantId: number;
  publicId: string;
  tourRoomAllocationId: number;
  visaApplicationId: number;
  itsNo: string;
  applicantName: string;
  isActive: boolean;
}

export interface RoomAllocationApi {
  tourRoomAllocationId: number;
  publicId: string;
  roomSelectionPublicId: string;
  venueName: string;
  propertyName: string;
  roomTypeName: string;
  minCapacity: number;
  maxCapacity: number;
  minAdults: number;
  maxAdults: number;
  maxChildren: number;
  roomAllocated: number;
  status: string;
  isActive: boolean;
  applicants: RoomAllocationApplicantApi[];
}

export interface TourDetailApi {
  tourId: number;
  parent_id?: string | null;
  publicId: string;
  tourCode: string;  
  tourName: string;
  tourType: number;
  tourCategory: number | null;

  rotationId: number | null;
  rotationName: string | null;

  tourOperatorId: number;
  tourOperatorName: string;

  noOfApplicants: number;
  faizAccommodation: boolean;

  accommodationPreference?: number;
  specialAccommodationRequest?: string;
  notes: string;

  // Arrival
  arrivalTransportMode: number;
  arrivalFlightNo: string;
  arrivalTime: string;
  arrivalPortId: number;
  arrivalPortName: string;
  arrivalDate: string;
  arrivalBookingRef: string;
  arrivalAirline: string;

  // Departure
  departureTransportMode: number;
  departureFlightNo: string;
  departureTime: string;
  departurePortId: number;
  departurePortName: string;
  departureDate: string;
  departureBookingRef: string;
  departureAirline: string;

  isArrivalConfirmed: boolean;
  isDepartureConfirmed: boolean;

  status: number;
  isLocked: boolean;
  statusName: string;  
  approvalStatus: number;
  isActive: boolean;

  createdBy: number;
  createdAt: string;
  updatedBy: number | null;
  updatedAt: string | null;
  

  venueSchedules: {
    tourVenueScheduleId: number;
    publicId: string;
    tourId: number;
    venueId: number;
    venueName: string;
    hotelPropertyId?: number;
    hotelName?: string;
    checkInDate: string;
    checkInTime: string;
    checkOutDate: string;
    checkOutTime: string;
  }[];

  roomAvailabilities: RoomAvailabilityApi[];
  roomAllocations: RoomAllocationApi[];
  tourAvailabilityRecommendations: TourAvailabilityRecommendationApi[];
  mandatoryServices: TourServiceApi[];
  optionalServices: OptionalApplicantServiceApi[];
}

export interface UpdateTourPayload {
  parent_id: string | null;
  tourName: string;
  tourType: number;
  tourCategory: number | null;
  rotationId: number | string | null;
  tourOperatorId: number;
  noOfApplicants: number;
  faizAccommodation: boolean;

  accommodationPreference?: number;
  specialAccommodationRequest?: string;
  termsAndConditions?: string;
  notes?: string;

  arrivalTransportMode: number;
  arrivalFlightNo: string;
  arrivalTime: string;
  arrivalPortId: number;
  arrivalDate: string;
  arrivalBookingRef: string;
  arrivalAirline: string;

  departureTransportMode: number;
  departureFlightNo: string;
  departureTime: string;
  departurePortId: number;
  departureDate: string;
  departureBookingRef: string;
  departureAirline: string;

  isArrivalConfirmed: boolean;
  isDepartureConfirmed: boolean;

  venueSchedules: {
    venueId: number;
    hotelPropertyId?: number;
    hotelName?: string;
    checkInDate: string;
    checkInTime: string;
    checkOutDate: string;
    checkOutTime: string;
  }[];

  updatedBy: number; 
}

export interface PropertyApi {
  propertyId: number;
  publicId: string;
  venueName: string;
  propertyName: string;
  propertyCode: string;
  propertyGrade: string;
  propertyTypeName: string;
  phoneNumber: string;
  checkInTime: string;
  checkOutTime: string;
  totalFloors: number;
  totalRooms: number;
  maxAdults: number;
  maxChildren: number;
  capacity: number;
  isActive: boolean;
}

export interface PropertyRoomTypeApi {
  publicId: string;
  propertyRoomTypeId: number;
  roomTypeId: number;
  roomTypeName: string;
  numberOfRooms: number;
  maxAdults: number;
  maxChildren: number;
  maxOccupancy: number;
  minAdults: number;
  minOccupancy: number;
  roomNames: string;
  roomNamesArray: string[];
  amenityIds: number[];
}

export interface PropertyDetailApi {
  propertyId: number;
  publicId: string;

  venueId: number;
  venueName: string;

  propertyName: string;
  propertyCode: string;

  propertyGradeId: number;
  propertyGrade: string;

  propertyTypeId: number;
  propertyTypeName: string;

  address: string;
  primaryContact: string;
  primaryEmail: string;
  phoneNumber: string;

  checkInTime: string;
  checkOutTime: string;

  childAgeMax: number;

  contacts: {
    name: string;
    label: string;
    email: string;
    phone: string;
  }[];

  amenityIds: number[];

  roomTypes: PropertyRoomTypeApi[];
}

export interface PropertyAvailabilityDateRangeApi {
  dateRangeIndex: number;
  dateLabel: string;
  startDate: string;
  endDate: string;
  status: number;
  numberOfRooms: number;
}

export interface PropertyAvailabilityRoomConfigApi {
  propertyRoomTypeId: number;
  roomPublicId: string;
  roomTypeId: number;
  roomTypeName: string;
  roomNames: string;
  roomNamesArray: string[];
  numberOfRooms: number;
  blockedRooms: number;
  availableRooms: number;
}

export interface PropertyAvailabilityApi {
  publicId: string;
  propertyId: number;
  propertyPublicId: string;
  propertyName: string;

  venueId: number;
  venueName: string;

  propertyTypeName: string;
  isActive: boolean;
  isReleased: boolean;

  dateRanges: PropertyAvailabilityDateRangeApi[];
  dateLabels: string[];

  roomConfigs: PropertyAvailabilityRoomConfigApi[];

  availableTourOperatorTypeIds: number[];
}

export interface PropertyAvailabilityListResponse {
  totalCount: number;
  items: PropertyAvailabilityApi[];
}

export interface PropertyAvailabilityDetailDateRangeApi {
  dateRangeIndex: number;
  dateLabel: string;
  startDate: string;
  endDate: string;
  status: number;
  numberOfRooms: number;
}

export interface PropertyAvailabilityDetailRoomConfigApi {
  propertyRoomTypeId: number;
  roomPublicId: string;
  roomTypeName: string;
  numberOfRooms: number;
}

export interface PropertyAvailabilityDetailApi {
  publicId: string;
  propertyId: number;
  propertyName: string;
  propertyTypeName: string;
  isActive: boolean;
  isReleased: boolean;
  dateRanges: PropertyAvailabilityDetailDateRangeApi[];
  dateLabels: string[];
  roomConfigs: PropertyAvailabilityDetailRoomConfigApi[];
  availableTourOperatorTypeIds: number[];

}

export interface GeneralMasterApi {
  id: number;
  code: string;
  value: string;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface GeneralMasterByCodeApi {
  id: number;
  code: string;
  value: string;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface InstantAvailabilityRequest {
  noOfApplicants: number;
  tourOperatorTypeId: number;
  venueSchedules: {
    venueId: number;
    checkInDate: string;
    checkInTime: string;
    checkOutDate: string;
    checkOutTime: string;
  }[];
}

export interface InstantAvailabilityRoomType {
  propertyRoomTypeId: number;
  roomTypeName: string;
  minCapacity: number;
  maxCapacity: number;
  overallAvailableRooms: number;
  requestedRooms: number;
  totalCapacity: number;
}

export interface InstantAvailabilityProperty {
  venueId: number;
  venueName: string;
  propertyId: number;
  propertyName: string;
  checkInDate: string;
  checkOutDate: string;
  totalRequestedRooms: number;
  isAvailableForEntireStay: boolean;
  roomTypes: InstantAvailabilityRoomType[];
}

export interface InstantAvailabilityVenue {
  venueId: number;
  venueName: string;
  checkInDate: string;
  checkOutDate: string;
  properties: InstantAvailabilityProperty[];
}

export interface UpdateTourStatusPayload {
  status: number;
  updatedBy: number;
}

export interface TourActivityLogApi {
  process: string;
  details: string;
  dateTime: string;
  doneBy: string;
  status: string;
}

export interface TourActivityLogResponse {
  tourId: number;
  publicId: string;
  tourCode: string;
  tourName: string;
  arrivalDate: string;
  departureDate: string;
  currentStatus: number;
  currentStatusName: string;
  activityLogs: TourActivityLogApi[];
}

export interface SendTourConversationPayload {
  tourPublicId: string;
  message: string;
  isInternal: boolean;
}

export interface TourChat {
  tourConversationId: number;
  publicId: string;
  tourPublicId: string;
  tourId: number;
  senderUserId: number;
  senderName: string;
  isAdmin: boolean;
  officeName: string;
  permanentAddress: string;
  message: string;
  isInternal: boolean;
  createdOn: string;
}

export interface GetTourChatsResponse {
  success: boolean;
  message: string;
  data: TourChat[];
}

export interface InstantCancellationPolicy {
  lawazimPublicId: string;
  freeCancelHours: number;
}

export interface BookingCharge {
  venueRowNo: number;
  roomSequence: number;

  propertyPublicId: string;
  propertyName: string;

  propertyRoomTypePublicId: string;

  lawazimPublicId: string;
  lawazimCode: string;
  lawazimName: string;

  requestedCheckIn: string;
  requestedCheckOut: string;

  quantity: number;

  calculationMethod: number;
  chargeType: number;

  noOfNights: number;
  billableDays: number;

  basePrice: number;
  earlyCheckInPrice: number;
  lateCheckOutPrice: number;

  extraLateCheckoutCharge: number;
  extraDayCharge: number;

  cancellationCharge: number;
  roomBookingAmount: number;
  firstNightAmount: number;

  cancellationTierId: number;
  cancellationBasedOn: number;
  cancellationPenaltyPercent: number;

  serviceCharge: number;
  totalCharge: number;

  basedOnDisplayName: string;
}

export interface BookingCancellationPolicy {
  lawazimPublicId: string;
  freeCancelHours: number;
}

export interface BookingCancellationTier {
  lawazimCancellationTierPublicId: string;
  daysBeforeCheckIn: number;
  basedOn: number;
  penaltyValue: number;
  basedOnDisplayName: string;
}

export interface BookingChargesResponse {
  charges: BookingCharge[];
  cancellationPolicies: BookingCancellationPolicy[];
  cancellationTiers: BookingCancellationTier[];
}

export interface BookingChargesPayload {
  chargeCalculationMode: number;
  cancellationScope: number;
  tourPublicId?: string;
  tourId?: number;

  venues?: {
    venueRowNo: number;
    propertyPublicId: string;
    checkInDate: string;
    checkInTime: string;
    checkOutDate: string;
    checkOutTime: string;
    rooms: {
      roomSequence: number;
      propertyRoomTypePublicId: string;
      quantity: number;
    }[];
  }[];

  propertyTypes?: {
    venueRowNo: number;
    roomSequence: number;
    propertyTypePublicId: string;
    infantCount: number;
    childCount: number;
    adultCount: number;
    seniorCount: number;
  }[];

  transport?: {
    venueRowNo: number;
    portPublicId: string;
    transportType: number;
    infantCount: number;
    childCount: number;
    adultCount: number;
    seniorCount: number;
  }[];

  interVenue?: {
    venueRowNo: number;
    fromVenuePublicId: string;
    toVenuePublicId: string;
    infantCount: number;
    childCount: number;
    adultCount: number;
    seniorCount: number;
  }[];

  atraaf?: {
    venueRowNo: number;
    atraafPublicId: string;
    infantCount: number;
    childCount: number;
    adultCount: number;
    seniorCount: number;
  }[];

  additionalServices?: {
    venueRowNo: number;
    tourServicePublicId: string;
    quantity: number;
    infantCount: number;
    childCount: number;
    adultCount: number;
    seniorCount: number;
  }[];
}


export interface PropertyRoomTypeApi {
  publicId: string;
  propertyRoomTypeId: number;
  roomTypeId: number;
  roomTypeName: string;
  numberOfRooms: number;
  maxAdults: number;
  maxChildren: number;
  maxOccupancy: number;
  childAgeMax: number;
  minAdults: number;
  minOccupancy: number;
  rentPerRoom: number;
}

export interface PropertyDetailApi {
  propertyId: number;
  publicId: string;
  propertyName: string;

  checkInTime: string;
  checkOutTime: string;

  roomTypes: PropertyRoomTypeApi[];
}

export interface HotelPropertyApi {
  propertyId: number;
  publicId: string;
  venueName: string;
  propertyName: string;
  propertyCode: string;
}

export interface CancelTourResponse {
  success: boolean;
  message: string;
  data: boolean;
  errors: string[];
}

export interface TourBookingReceiptEntry {
  publicId: string;
  lawazimType: number;
  lawazimTypeName: string;
  transactionType: number;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  occurredOn: string;
}

export interface TourBookingReceipt {
  tourPublicId: string;
  tourCode: string;
  tourName: string;
  venues: string[];
  tourType: number;
  tourCategory: number;
  tourOperatorId: number;
  tourOperatorName: string;
  noOfApplicants: number;
  arrivalDate: string;
  departureDate: string;
  grossAmount: number;
  deductionAmount: number;
  netAmount: number;
  entries: TourBookingReceiptEntry[];
}

export interface TourBookingReceiptResponse {
  success: boolean;
  message: string;
  data: TourBookingReceipt;
  errors: string[];
}

export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getBufferConfigurations = async (): Promise<BufferConfig[]> => {
  const res = await axiosInstance.get("/buffer-configuration", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getVenues = async (): Promise<VenueApi[]> => {
  const res = await axiosInstance.get("/Venues?onlyActive=true", {
    headers: getAuthHeader(),
  });

  return res.data; 
};

export const getPorts = async (): Promise<PortApi[]> => {
  const res = await axiosInstance.get("/Ports", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getMiqaatByOperator = async (
  tourOperatorId: number
): Promise<MiqaatApi[]> => {
  const res = await axiosInstance.get<MiqaatApi[]>(
    `/miqaat/by-tour/${tourOperatorId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getCutoffConfigurations = async (): Promise<CutoffConfig[]> => {
  const res = await axiosInstance.get("/cutoff-configuration", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getTours = async ({
  searchText,
  pageNo,
  pageSize,
  tourOperatorId,
}: {
  searchText?: string | null;
  pageNo: number;
  pageSize: number;
  tourOperatorId?: number;
}): Promise<TourListResponse> => {
  const res = await axiosInstance.get("/tours/list", {
    headers: getAuthHeader(),
    params: {
      searchText,
      pageNo,
      pageSize,
      tourOperatorId,
    },
  });

  return res.data;
};

export const createTour = async (
  payload: CreateTourPayload,
): Promise<{ tourId: number, tourCode: string }> => {
  const res = await axiosInstance.post("/tours", payload, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getPortByPublicId = async (
  publicId: string
): Promise<PortDetailApi> => {
  const res = await axiosInstance.get(
    `/Ports/${publicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourOperatorWithDocuments = async (
  operatorId: number
): Promise<TourOperatorWithDocumentsApi> => {
  const res = await axiosInstance.get(
    `/tour-operator/${operatorId}/with-documents`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourStats = async (): Promise<TourStatsApi> => {
  const res = await axiosInstance.get("/tours/stats", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getIntercityCheckouts = async (): Promise<IntercityCheckoutApi[]> => {
  const res = await axiosInstance.get(
    "/InterCityCheckouts",
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;  
};

export const getTourByPublicId = async (
  publicId: string
): Promise<TourDetailApi> => {
  const res = await axiosInstance.get(`/tours/${publicId}`, {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const updateTour = async (
  publicId: string,
  payload: UpdateTourPayload
): Promise<void> => {
  await axiosInstance.put(`/tours/${publicId}`, payload, {
    headers: getAuthHeader(),
  });
};

export const getProperties = async (): Promise<PropertyApi[]> => {
  const res = await axiosInstance.get("/property/list", {
    headers: getAuthHeader(),
  });

  return res.data.items;
};

export const getPropertyByPublicId = async (
  publicId: string
): Promise<PropertyDetailApi> => {
  const res = await axiosInstance.get(
    `/property/${publicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getPropertyAvailability = async ({
  pageNo,
  pageSize,
}: {
  pageNo: number;
  pageSize: number;
}): Promise<PropertyAvailabilityListResponse> => {
  const res = await axiosInstance.get(
    "/property-availability/list",
    {
      headers: getAuthHeader(),
      params: {
        pageNo,
        pageSize,
      },
    }
  );

  return res.data;
};

export const getPropertyAvailabilityByPublicId = async (
  publicId: string
): Promise<PropertyAvailabilityDetailApi> => {
  const res = await axiosInstance.get(
    `/property-availability/${publicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getGeneralMasters = async (): Promise<GeneralMasterApi[]> => {
  const res = await axiosInstance.get("/general-master", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getGeneralMasterByCode = async (
  code: string
): Promise<GeneralMasterByCodeApi> => {
  const res = await axiosInstance.get(
    `/general-master/by-code/${code}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getInstantBookingAvailability = async (
  payload: InstantAvailabilityRequest,
): Promise<InstantAvailabilityVenue[]> => {
  const res = await axiosInstance.post(
    "/property-availability/get-availability",
    payload,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const updateTourStatus = async (
  publicId: string,
  updatedBy: number
): Promise<void> => {
  await axiosInstance.post(
    `/tours/${publicId}/send-for-approval`,
    null,
    {
      params: {
        updatedBy,
      },
      headers: getAuthHeader(),
    }
  );
};
 
export const requestVenueAvailability = async (
  recommendPublicId: string
) => {
  const res = await axiosInstance.post(
    `/tours/venue-availability/${recommendPublicId}/accept-recommend-dates`,
    null,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourActivityLogs = async (
  publicId: string
): Promise<TourActivityLogResponse> => {
  const res = await axiosInstance.get(
    `/tours/${publicId}/activity-logs`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourChats = async (tourPublicId: string): Promise<GetTourChatsResponse> => {
  const response = await axiosInstance.get<GetTourChatsResponse>(
    `/tour-conversation/${tourPublicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return response.data;
};

export const tourChatsubmittion = async (
  payload: SendTourConversationPayload
) => {
  const response = await axiosInstance.post(
    "/tour-conversation/send",
    payload,
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  ); 

  return response.data;
};

export const getBookingCharges = async (
  payload: BookingChargesPayload,
): Promise<BookingChargesResponse> => {
  const res = await axiosInstance.post(
    "/Lawazim/booking-charges",
    payload,
  );

  return res.data.data;
};

export const getProperty = async (
  publicId: string,
): Promise<PropertyDetailApi> => {
  const res = await axiosInstance.get(`/property/${publicId}`);

  return res.data;
};

export const getHotelsByVenue = async (
  venuePublicId: string,
): Promise<HotelPropertyApi[]> => {
  const res = await axiosInstance.get("/property/hotel-list", {
    headers: getAuthHeader(),
    params: {
      venuePublicId,
    },
  });

  return res.data.data;
};

export const cancelTour = async (
  publicId: string
): Promise<CancelTourResponse> => {
  const res = await axiosInstance.patch(
    `/tours/${publicId}/cancel`,
    null,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getTourBookingReceipt = async (
  tourPublicId: string
): Promise<TourBookingReceipt> => {
  const res = await axiosInstance.get<TourBookingReceiptResponse>(
    `/tours/${tourPublicId}/tour-booking-receipt`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data.data;
};

export const downloadTourBookingReceiptPdf = async (
  tourPublicId: string
): Promise<{ blob: Blob; fileName: string }> => {
  const res = await axiosInstance.get(
    `/tours/${tourPublicId}/tour-booking-receipt/pdf`,
    {
      headers: {
        ...getAuthHeader(),
        Accept: "application/pdf",
      },
      responseType: "blob",
    }
  );

  const contentDisposition = res.headers["content-disposition"];

  let fileName = `tour-booking-receipt-${tourPublicId}.pdf`;

  if (contentDisposition) {
    const utf8Match = contentDisposition.match(
      /filename\*=UTF-8''([^;]+)/
    );

    const normalMatch = contentDisposition.match(
      /filename="?([^"]+)"?/
    );

    if (utf8Match?.[1]) {
      fileName = decodeURIComponent(utf8Match[1]);
    } else if (normalMatch?.[1]) {
      fileName = normalMatch[1];
    }
  }

  return {
    blob: res.data,
    fileName,
  };
};